import React, { MutableRefObject } from "react";
import Checkbox from "antd/lib/checkbox";
import Radio from "antd/lib/radio";
import Form, { RuleObject } from "antd/lib/form";
import { SurveyInput } from "pages/symptoms/surveyForms/surveyInput";
import {
  SurveyQuestionCard,
  AnswerList,
  AnswerOption,
  FormItemStyle,
  AsteriskStyle,
  CardTitle
} from "pages/symptoms/surveyForms/surveyFormComponents";

interface KidneyStoneSectionProps {
  readonly currentPage: number;
  setOtherKidneyStoneLastExperienceTempInput(input: string): void;
  readonly otherKidneyStoneLastExperienceTempInput: string;
  readonly otherKidneyStoneLastExperienceTempInputRef: MutableRefObject<string>;
  readonly otherDurationKnownAboutKidneyStoneTempInput: string;
  setOtherDurationKnownAboutKidneyStoneTempInput(input: string): void;
  readonly otherDurationKnownAboutTempInputRef: MutableRefObject<string>;
  setShowSubmit(show: boolean): void;
}

export const KidneyStoneSection: React.FC<KidneyStoneSectionProps> = ({
  currentPage,
  otherKidneyStoneLastExperienceTempInputRef,
  otherKidneyStoneLastExperienceTempInput,
  setOtherKidneyStoneLastExperienceTempInput,
  otherDurationKnownAboutKidneyStoneTempInput,
  setOtherDurationKnownAboutKidneyStoneTempInput,
  otherDurationKnownAboutTempInputRef,
  setShowSubmit
}) => {
  const validateLastExperienceOfKidneyStone = (
    rules: RuleObject,
    value: string,
    callback: (error?: string) => void
  ) => {
    if (
      otherKidneyStoneLastExperienceTempInputRef.current === "" &&
      localStorage.getItem("lastExperienceOfKidneyStones")?.includes("Other")
    ) {
      callback("Please enter value for 'Other' field.");
    } else {
      callback();
    }
  };

  const validateDurationKnownOfKidneyStone = (
    rules: RuleObject,
    value: string,
    callback: (error?: string) => void
  ) => {
    if (
      otherDurationKnownAboutTempInputRef.current === "" &&
      localStorage.getItem("durationKnownAboutYourKidney")?.includes("Other")
    ) {
      callback("Please enter value for 'Other' field.");
    } else {
      callback();
    }
  };

  return (
    <>
      {currentPage === 7 && (
        <SurveyQuestionCard
          sectionTag="Kidney Stone"
          title="Have you ever had a kidney stone?"
          required
        >
          <FormItemStyle
            name="Have you ever had a kidney stone"
            label=""
            rules={[{ required: true }]}
            initialValue={""}
          >
            <Radio.Group
              value={localStorage.getItem("hadAKidneyStone")}
              onChange={e => {
                if (e.target.value === "No") {
                  setShowSubmit(true);
                } else {
                  setShowSubmit(false);
                }
                localStorage.setItem("hadAKidneyStone", e.target.value);
              }}
            >
              <AnswerList>
                <AnswerOption value="Yes">Yes</AnswerOption>
                <AnswerOption value="No">No</AnswerOption>
              </AnswerList>
            </Radio.Group>
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 8 && (
        <SurveyQuestionCard
          sectionTag="Kidney Stone Symptoms"
          title="When did you last experience symptoms from your kidney stone?"
          required
        >
          <FormItemStyle
            name="Last experience of symptoms"
            label=""
            initialValue={""}
            rules={[
              { required: true },
              {
                validator: validateLastExperienceOfKidneyStone,
                validateTrigger: "submit"
              }
            ]}
          >
            <Checkbox.Group
              style={{ width: "100%" }}
              onChange={e =>
                localStorage.setItem(
                  "lastExperienceOfKidneyStones",
                  e.toString()
                )
              }
              value={sessionStorage
                .getItem("lastExperienceOfKidneyStones")
                ?.split(/,/)}
            >
              <AnswerList>
                <AnswerOption value="In the last 2 weeks" isCheckbox>
                  In the last 2 weeks
                </AnswerOption>
                <AnswerOption value="In the last 3 months" isCheckbox>
                  In the last 3 months
                </AnswerOption>
                <AnswerOption value="In the last 3 - 6 months" isCheckbox>
                  In the last 3 - 6 months
                </AnswerOption>
                <AnswerOption value="In the last 6 - 12 months" isCheckbox>
                  In the last 6 - 12 months
                </AnswerOption>
                <AnswerOption value="More than 1 year ago" isCheckbox>
                  More than 1 year ago
                </AnswerOption>
                <AnswerOption value="Other" isCheckbox>
                  <div style={{ width: "100%" }}>
                    <span>Other</span>
                    <SurveyInput
                      value={
                        otherKidneyStoneLastExperienceTempInput ||
                        otherKidneyStoneLastExperienceTempInputRef.current
                      }
                      placeholder="Please specify"
                      onChange={e => {
                        setOtherKidneyStoneLastExperienceTempInput(
                          e.target.value
                        );
                        otherKidneyStoneLastExperienceTempInputRef.current =
                          e.target.value;
                      }}
                      style={{ marginTop: "8px", width: "100%" }}
                    />
                  </div>
                </AnswerOption>
              </AnswerList>
            </Checkbox.Group>
          </FormItemStyle>
        </SurveyQuestionCard>
      )}

      {currentPage === 9 && (
        <SurveyQuestionCard
          sectionTag="Duration of Kidney Stone"
          title="How long have you known about your kidney stone?"
          required
        >
          <FormItemStyle
            name="How long have you known about your kidney stone"
            label=""
            initialValue={""}
            rules={[
              { required: true },
              {
                validator: validateDurationKnownOfKidneyStone,
                validateTrigger: "submit"
              }
            ]}
          >
            <Checkbox.Group
              style={{ width: "100%" }}
              onChange={e =>
                localStorage.setItem(
                  "durationKnownAboutYourKidney",
                  e.toString()
                )
              }
              value={sessionStorage
                .getItem("durationKnownAboutYourKidney")
                ?.split(/,/)}
            >
              <AnswerList>
                <AnswerOption value="2 weeks" isCheckbox>
                  2 weeks
                </AnswerOption>
                <AnswerOption value="3 months" isCheckbox>
                  3 months
                </AnswerOption>
                <AnswerOption value="4 - 6 months" isCheckbox>
                  4 - 6 months
                </AnswerOption>
                <AnswerOption value="More than 1 year" isCheckbox>
                  More than 1 year
                </AnswerOption>
                <AnswerOption value="Other" isCheckbox>
                  <div style={{ width: "100%" }}>
                    <span>Other</span>
                    <SurveyInput
                      value={
                        otherDurationKnownAboutKidneyStoneTempInput ||
                        otherDurationKnownAboutTempInputRef.current
                      }
                      placeholder="Please specify"
                      onChange={e => {
                        setOtherDurationKnownAboutKidneyStoneTempInput(
                          e.target.value
                        );
                        otherDurationKnownAboutTempInputRef.current =
                          e.target.value;
                      }}
                      style={{ marginTop: "8px", width: "100%" }}
                    />
                  </div>
                </AnswerOption>
              </AnswerList>
            </Checkbox.Group>
          </FormItemStyle>
        </SurveyQuestionCard>
      )}
    </>
  );
};
